/**
 * InboxThreadPage - WhatsApp-style conversation view
 *
 * Phase 4 - Inbox v2 (Conversation bubbles)
 * Shows BOTH sent and received items as chat bubbles.
 * Route: /inbox/:username (captures @username, strips @ internally)
 *
 * Features:
 * - Chat bubble layout (left=incoming, right=outgoing)
 * - Newest messages at bottom
 * - Auto-scroll to bottom on load
 * - Mark as read when opened
 */

import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, MessageSquare, ListChecks, Film, Loader2, ExternalLink, Send, Check, Star, ThumbsDown } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useUserLibrary } from '../contexts/UserLibraryContext';
import { useInboxConversation, useMarkInboxThreadRead, type ConversationItem } from '../hooks/useInboxQuery';
import { useBackNavigation } from '../hooks/useBackNavigation';
import { queryKeys } from '../lib/queryKeys';
import { apiFetch } from '../lib/api';
import { formatDistanceToNow } from '../utils/dateUtils';
import { MessageBubbleSkeleton } from '../components/Skeleton';
import { RatingModal } from '../components/RatingModal';
import { useToast } from '../components/ui/Toast';
import type { MediaItem } from '../types/movie';

// Type for user profile lookup (matches /users/by-username/:username FOREVER CONTRACT)
interface UserProfile {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  visibility?: 'public' | 'friends' | 'private';
}

export function InboxThreadPage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/inbox');
  const { user, session, profile } = useAuth();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  // Strip @ from username if present
  const cleanUsername = username?.startsWith('@') ? username.slice(1) : username;

  // Step 1: Resolve username to user ID (FOREVER CONTRACT: flat object, not wrapped)
  const { data: otherUser, isLoading: isLoadingUser, error: userError } = useQuery({
    queryKey: queryKeys.users.byUsername(cleanUsername?.toLowerCase() || ''),
    queryFn: async () => {
      if (!cleanUsername) return null;
      return apiFetch<UserProfile>(`/users/by-username/${cleanUsername.toLowerCase()}`);
    },
    enabled: !!cleanUsername,
    staleTime: 5 * 60_000, // 5 minutes
  });

  // Step 2: Fetch conversation items (both sent and received)
  const {
    items,
    isLoading: isLoadingItems,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInboxConversation({
    otherUserId: otherUser?.id || '',
    limit: 50,
  });

  // Step 3: Mark thread as read when opened
  const { mutate: markAsRead } = useMarkInboxThreadRead();
  const hasMarkedReadRef = useRef<string | null>(null);

  useEffect(() => {
    // Mark as read when we have the user ID and haven't already marked this thread
    if (otherUser?.id && hasMarkedReadRef.current !== otherUser.id) {
      hasMarkedReadRef.current = otherUser.id;
      markAsRead(otherUser.id);
    }
  }, [otherUser?.id, markAsRead]);

  // Auto-scroll to bottom when items load (only on initial load)
  useLayoutEffect(() => {
    if (!isLoadingItems && items.length > 0 && !hasScrolledRef.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      hasScrolledRef.current = true;
    }
  }, [isLoadingItems, items.length]);

  // Handle item click based on type
  const handleItemClick = (item: ConversationItem) => {
    if (item.type === 'recommendation' && item.media_id && item.media_type) {
      navigate(`/${item.media_type}/${item.media_id}`);
    } else if (item.type === 'list_shared' && item.watchlist_id) {
      navigate(`/list/${item.watchlist_id}`);
    }
  };

  // ============================================================================
  // Recommendation Feedback UI (Phase 5)
  // ============================================================================

  const queryClient = useQueryClient();
  const { isWatched, getRating, toggleWatched, setRating } = useUserLibrary();
  const { toast } = useToast();

  // Rating modal state
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [ratingModalItem, setRatingModalItem] = useState<ConversationItem | null>(null);

  // Local dismissed overrides for instant feedback (optimistic)
  const [dismissedOverrides, setDismissedOverrides] = useState<Set<string>>(new Set());

  // Dismiss recommendation mutation
  const dismissMutation = useMutation({
    mutationFn: async (recommendationId: string) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      return apiFetch<{ success: boolean }>(`/recommendations/${recommendationId}`, {
        method: 'PATCH',
        token: session.access_token,
        body: { is_dismissed: true },
      });
    },
    onMutate: async (recommendationId) => {
      // Optimistic update
      setDismissedOverrides((prev) => new Set(prev).add(recommendationId));
    },
    onError: (_err, recommendationId) => {
      // Rollback on error
      setDismissedOverrides((prev) => {
        const next = new Set(prev);
        next.delete(recommendationId);
        return next;
      });
      toast.error('Failed to dismiss');
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      if (user?.id && otherUser?.id) {
        queryClient.invalidateQueries({ queryKey: ['inbox', user.id, 'conversation', otherUser.id] });
        queryClient.invalidateQueries({ queryKey: queryKeys.inbox.threadsBase(user.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.inbox.threadsPreview(user.id, { limit: 5 }) });
        queryClient.invalidateQueries({ queryKey: queryKeys.inbox.unreadCount(user.id) });
      }
    },
  });

  // Handle watched toggle
  const handleWatched = useCallback(async (item: ConversationItem) => {
    if (!item.media_id || !item.media_type || !item.recommendation) return;

    const wasWatched = isWatched(item.media_id, item.media_type);
    try {
      const result = await toggleWatched(
        item.media_id,
        item.media_type,
        item.recommendation.media_title ?? 'Unknown',
        item.recommendation.media_poster ?? undefined,
        item.recommendation.media_year ?? undefined
      );
      if (result) {
        toast.success(wasWatched ? 'Removed from Watched' : 'Marked as watched');
      }
    } catch (err) {
      console.error('Error toggling watched:', err);
      toast.error('Something went wrong');
    }
  }, [isWatched, toggleWatched, toast]);

  // Handle rate click - open modal
  const handleRate = useCallback((item: ConversationItem) => {
    setRatingModalItem(item);
    setRatingModalOpen(true);
  }, []);

  // Handle rating completed - sync rating to UserLibraryContext for instant UI update
  const handleRated = useCallback(async (rating: number) => {
    if (ratingModalItem?.media_id && ratingModalItem?.media_type) {
      // Update UserLibraryContext so getRating() returns the new value immediately
      await setRating(ratingModalItem.media_id, ratingModalItem.media_type, rating);
    }
    setRatingModalOpen(false);
    setRatingModalItem(null);
    toast.success('Rating saved');
  }, [ratingModalItem, setRating, toast]);

  // Handle dismiss (not interested)
  const handleDismiss = useCallback((item: ConversationItem) => {
    if (!item.recommendation_id) return;
    dismissMutation.mutate(item.recommendation_id);
  }, [dismissMutation]);

  // Redirect if not logged in
  if (!user) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-400">Please sign in to view your inbox</p>
        </div>
      </div>
    );
  }

  // Show loading while resolving username
  if (isLoadingUser) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-slate-500" />
      </div>
    );
  }

  // Show error if user not found
  if (userError || !otherUser) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-400">User not found</p>
          <button
            onClick={goBack}
            className="text-sm text-teal-400 hover:text-teal-300 mt-2 inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} />
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[#0A0D10]">
      {/* Header - sticky within scroll container */}
      <div className="flex-shrink-0 sticky top-0 z-20 bg-[#0F1419] border-b border-white/[0.06]">
        <div className="px-4 py-4">
          <div className="flex items-center gap-4">
            {/* Back button - only visible on mobile */}
            <button
              onClick={goBack}
              className="flex-shrink-0 p-2 -ml-2 text-slate-400 hover:text-white transition-colors md:hidden"
              aria-label="Go back"
            >
              <ArrowLeft size={20} />
            </button>

            {/* User info */}
            <Link
              to={`/u/${otherUser.username}`}
              className="flex items-center gap-3 min-w-0 hover:opacity-80 transition-opacity"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                {otherUser.avatar_url ? (
                  <img
                    src={otherUser.avatar_url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={20} className="text-slate-400" />
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-white truncate">
                  {otherUser.display_name || otherUser.username}
                </h1>
                <p className="text-sm text-slate-400 truncate">
                  @{otherUser.username}
                </p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Conversation - scrollable area */}
      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        <div className="max-w-4xl mx-auto w-full px-4 pt-4 pb-24 md:pb-6">
          {/* Load more older messages button */}
          {hasNextPage && (
            <div className="text-center mb-4">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-slate-300 disabled:opacity-50 transition-colors"
              >
                {isFetchingNextPage ? (
                  <Loader2 size={16} className="mx-auto animate-spin" />
                ) : (
                  'Load older messages'
                )}
              </button>
            </div>
          )}

          {isLoadingItems ? (
            <div className="space-y-3">
              <MessageBubbleSkeleton isOutgoing={false} />
              <MessageBubbleSkeleton isOutgoing={true} />
              <MessageBubbleSkeleton isOutgoing={false} />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare size={48} className="mx-auto mb-4 text-slate-600" />
              <h2 className="text-lg font-medium text-slate-300 mb-2">
                No messages yet
              </h2>
              <p className="text-sm text-slate-500">
                Start by sending a recommendation!
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <MessageBubble
                  key={item.id}
                  item={item}
                  onClick={() => handleItemClick(item)}
                  isWatched={item.media_id && item.media_type ? isWatched(item.media_id, item.media_type) : false}
                  rating={item.media_id && item.media_type ? getRating(item.media_id, item.media_type) : null}
                  isDismissed={item.recommendation_id ? (dismissedOverrides.has(item.recommendation_id) || item.recommendation?.is_dismissed === true) : false}
                  onWatched={() => handleWatched(item)}
                  onRate={() => handleRate(item)}
                  onDismiss={() => handleDismiss(item)}
                  viewerAvatarUrl={profile?.avatar_url}
                  otherUserAvatarUrl={otherUser?.avatar_url}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rating Modal */}
      {ratingModalItem && ratingModalItem.media_id && ratingModalItem.media_type && ratingModalItem.recommendation && (
        <RatingModal
          movie={{
            id: ratingModalItem.media_id,
            title: ratingModalItem.recommendation.media_title ?? 'Unknown',
            poster_path: ratingModalItem.recommendation.media_poster,
            backdrop_path: null,
            release_date: ratingModalItem.recommendation.media_year ? `${ratingModalItem.recommendation.media_year}-01-01` : '',
            media_type: ratingModalItem.media_type,
            overview: '',
            genre_ids: [],
            vote_average: 0,
            vote_count: 0,
            popularity: 0,
            original_language: '',
          } as MediaItem}
          isOpen={ratingModalOpen}
          onClose={() => {
            setRatingModalOpen(false);
            setRatingModalItem(null);
          }}
          onRated={handleRated}
        />
      )}
    </div>
  );
}

// ============================================================================
// MessageBubble - Chat bubble component for conversation items
// ============================================================================

interface MessageBubbleProps {
  item: ConversationItem;
  onClick: () => void;
  isWatched: boolean;
  rating: number | null;
  isDismissed: boolean;
  onWatched: () => void;
  onRate: () => void;
  onDismiss: () => void;
  viewerAvatarUrl?: string | null;
  otherUserAvatarUrl?: string | null;
}

function MessageBubble({ item, onClick, isWatched, rating, isDismissed, onWatched, onRate, onDismiss, viewerAvatarUrl, otherUserAvatarUrl }: MessageBubbleProps) {
  const isOutgoing = item.direction === 'outgoing';
  const isIncomingRecommendation = !isOutgoing && item.type === 'recommendation';

  // Phase 5: handled = not interested OR (watched AND rated)
  const isHandled = isDismissed || (isWatched && rating !== null);
  const needsAction = isIncomingRecommendation && !isHandled;

  // Avatar for this message (incoming = other user, outgoing = viewer)
  const avatarUrl = isOutgoing ? viewerAvatarUrl : otherUserAvatarUrl;

  return (
    <div className={`flex items-end gap-2 ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
      {/* Incoming avatar (left side) */}
      {!isOutgoing && (
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <User size={12} className="text-slate-400" />
          )}
        </div>
      )}

      <div
        className={`max-w-[85%] ${
          isOutgoing
            ? 'bg-orange-500/20 ring-1 ring-orange-500/30 rounded-2xl rounded-br-md'
            : 'bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-2xl rounded-bl-md'
        }`}
      >
        {/* Direction indicator */}
        <div className={`flex items-center gap-2 px-4 py-2 border-b ${
          isOutgoing ? 'border-orange-500/20' : 'border-white/[0.06]'
        }`}>
          {isOutgoing ? (
            <>
              <Send size={12} className="text-orange-400" />
              <span className="text-xs font-medium text-orange-400">You sent</span>
            </>
          ) : (
            <>
              {item.type === 'recommendation' ? (
                <MessageSquare size={12} className="text-orange-400" />
              ) : (
                <ListChecks size={12} className="text-teal-400" />
              )}
              <span className={`text-xs font-medium ${
                item.type === 'recommendation' ? 'text-orange-400' : 'text-teal-400'
              }`}>
                {item.type === 'recommendation' ? 'Recommendation' : 'Shared List'}
              </span>
              {/* Action required badge - based on handled state, not read state */}
              {needsAction && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-500/20 text-amber-400 rounded">
                  Action required
                </span>
              )}
            </>
          )}
          <span className="text-xs text-slate-500 ml-auto">
            {formatDistanceToNow(item.created_at)}
          </span>
        </div>

        {/* Content */}
        <button
          onClick={onClick}
          className="w-full text-left hover:bg-white/[0.02] transition-colors"
        >
          {item.type === 'recommendation' && item.recommendation ? (
            <div className="flex gap-3 p-3">
              {/* Poster */}
              <div className="flex-shrink-0 w-12 h-18 bg-slate-800 rounded-lg overflow-hidden">
                {item.recommendation.media_poster ? (
                  <img
                    src={`https://image.tmdb.org/t/p/w154${item.recommendation.media_poster}`}
                    alt={item.recommendation.media_title || ''}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Film size={16} className="text-slate-600" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-white line-clamp-2">
                  {item.recommendation.media_title || 'Unknown title'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {item.media_type === 'tv' ? 'TV Show' : 'Movie'}
                  {item.recommendation.media_year && ` • ${item.recommendation.media_year}`}
                </p>
                {item.recommendation.message && (
                  <p className="text-sm text-slate-300 mt-2 line-clamp-2 italic">
                    "{item.recommendation.message}"
                  </p>
                )}
                {/* Taste alignment note for incoming recommendations */}
                {isIncomingRecommendation && !isHandled && (
                  <p className="text-[11px] text-slate-500 mt-2 leading-tight">
                    💡 Your response helps Filmple learn your taste.
                  </p>
                )}
              </div>

              <ExternalLink size={14} className="flex-shrink-0 text-slate-500 mt-1" />
            </div>
          ) : item.type === 'list_shared' && item.watchlist ? (
            <div className="flex gap-3 p-3">
              {/* Cover */}
              <div className="flex-shrink-0 w-12 h-12 bg-slate-800 rounded-lg overflow-hidden">
                {item.watchlist.cover_url ? (
                  <img
                    src={item.watchlist.cover_url}
                    alt={item.watchlist.name || ''}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-500/20 to-purple-500/20">
                    <ListChecks size={16} className="text-teal-400" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-white line-clamp-2">
                  {item.watchlist.name || 'Untitled list'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {item.watchlist.item_count ?? 0} items
                </p>
              </div>

              <ExternalLink size={14} className="flex-shrink-0 text-slate-500 mt-1" />
            </div>
          ) : (
            <div className="p-3">
              <p className="text-sm text-slate-400">{item.body || 'No content'}</p>
            </div>
          )}
        </button>

        {/* Feedback CTAs for incoming recommendations */}
        {isIncomingRecommendation && (
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-white/[0.06]">
            {/* Watched button */}
            {!isWatched ? (
              <button
                onClick={(e) => { e.stopPropagation(); onWatched(); }}
                className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-white bg-teal-500 rounded-md hover:bg-teal-400 transition-colors"
              >
                <Check size={12} />
                Mark watched
              </button>
            ) : (
              <span className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-teal-400 bg-teal-500/10 rounded-md">
                <Check size={12} />
                Watched
              </span>
            )}

            {/* Rate button */}
            {rating === null ? (
              <button
                onClick={(e) => { e.stopPropagation(); onRate(); }}
                className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-slate-300 hover:text-white bg-white/[0.06] rounded-md hover:bg-white/[0.1] transition-colors"
              >
                <Star size={12} />
                Rate
              </button>
            ) : (
              <span className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-yellow-400 bg-yellow-500/10 rounded-md">
                <Star size={12} className="fill-current" />
                {rating}/10
              </span>
            )}

            {/* Not interested button */}
            {!isDismissed ? (
              <button
                onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-slate-400 hover:text-red-400 bg-white/[0.06] rounded-md hover:bg-red-500/10 transition-colors"
              >
                <ThumbsDown size={12} />
                Not interested
              </button>
            ) : (
              <span className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-red-400 bg-red-500/10 rounded-md">
                <ThumbsDown size={12} />
                Not interested
              </span>
            )}
          </div>
        )}
      </div>

      {/* Outgoing avatar (right side) */}
      {isOutgoing && (
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <User size={12} className="text-slate-400" />
          )}
        </div>
      )}
    </div>
  );
}
